import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { ingressCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { SelectField, TextareaField, TextField } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Grid } from '@/ui/layout/Grid'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { StatusBadge } from '@/ui/Status/Status'
import { resourcesFor } from '../../api/resources'
import { ROUTES } from '../../constants/routes'
import { useWorkspaceAgentsQuery } from '../../hooks/queries'
import styles from './WorkflowsCard.module.css'

interface Props {
  workspaceId: string
  topicId: string
  /** Pre-filled event type (exact, or `prefix.*`). */
  suggestedEventType: string
}

interface WorkflowRow {
  id: string
  name: string
  data: Record<string, unknown>
  /** Set up by an agent for itself (its event_subscribe tool). */
  byAgent: boolean
}

/** What a workflow acts on, in one line. */
function trigger(data: Record<string, unknown>): string {
  const when = typeof data.when === 'string' ? data.when : null
  for (const op of ['equals', 'in', 'in_ci', 'contains', 'not_equals']) {
    const v = data[op]
    if (when && v != null)
      return `${when} ${op} ${Array.isArray(v) ? v.join(', ') : String(v)}`
  }
  return ingressCopy.workflows.everyEvent
}

/**
 * What happens to the events delivered into a topic: its workflows — an
 * agent, the events it acts on, and its instructions — whether people,
 * recipes or the agents themselves set them up. Without one, deliveries
 * land in the topic and nothing replies. The list ends with its add row,
 * which opens the form in place (D213: a list creates from its end).
 */
export function WorkflowsCard({
  workspaceId,
  topicId,
  suggestedEventType,
}: Props) {
  const qc = useQueryClient()
  const scope = { kind: 'topic', id: topicId } as const
  const workflowsKey = ['topic-workflows', topicId]
  const workflowsQuery = useQuery({
    queryKey: workflowsKey,
    queryFn: async (): Promise<WorkflowRow[]> =>
      (await resourcesFor(scope).list()).resources
        .filter((r) => r.kind === 'workflow')
        .map((r) => ({
          id: r.id,
          name: r.name,
          data: r.data as Record<string, unknown>,
          byAgent: r.author?.kind === 'agent',
        })),
  })
  const workflows = workflowsQuery.data ?? []
  const showSkeleton = useSkeletonDelay(workflowsQuery.isPending)
  const { data: agents = [] } = useWorkspaceAgentsQuery(workspaceId)
  const name = (id: unknown) =>
    agents.find((a) => a.agent_id === id)?.name ?? String(id ?? '?')

  const [adding, setAdding] = useState(false)
  const [agentId, setAgentId] = useState('')
  const [eventType, setEventType] = useState(suggestedEventType)
  const [instructions, setInstructions] = useState('')
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: workflowsKey })
  }
  const add = useMutation({
    mutationFn: () => {
      const type = eventType.trim()
      const text = instructions.trim()
      return resourcesFor(scope).create(
        'workflow',
        `${name(agentId)}: ${type}`,
        {
          agent_id: agentId,
          when: '{event_type}',
          equals: type,
          ...(text ? { instructions: text } : {}),
        },
      )
    },
    onSuccess: () => {
      closeForm()
      refresh()
    },
  })
  function closeForm() {
    setAdding(false)
    setAgentId('')
    setEventType(suggestedEventType)
    setInstructions('')
    add.reset()
  }
  const removeWorkflow = useMutation({
    mutationFn: (id: string) => resourcesFor(scope).delete(id),
    onSuccess: refresh,
  })
  const reasonOf = (e: unknown) => (e instanceof Error ? e.message : String(e))
  const failed = add.isError
    ? ingressCopy.workflows.addFailed(reasonOf(add.error))
    : removeWorkflow.isError
      ? ingressCopy.workflows.removeFailed(reasonOf(removeWorkflow.error))
      : null

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (agentId && eventType.trim()) add.mutate()
  }

  return (
    <Stack as="section" gap="space-12" aria-busy={workflowsQuery.isPending}>
      <SectionHeader rule trailingSpacing="compact">
        {ingressCopy.workflows.heading}
      </SectionHeader>
      {workflowsQuery.isPending ? (
        showSkeleton && <Skeleton lines={2} />
      ) : workflowsQuery.isError ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            {ingressCopy.workflows.loadFailed}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void workflowsQuery.refetch()}
            >
              {ingressCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : (
        <>
          {workflows.length === 0 && (
            <Alert tone="warning">{ingressCopy.workflows.none}</Alert>
          )}
          <RowList>
            {workflows.map((w) => (
              <RowListItem key={w.id}>
                <Link href={ROUTES.topicResourceEdit(topicId, w.id)}>
                  {w.name || name(w.data.agent_id)}
                </Link>
                <Text variant="code" tone="text-sub">
                  {trigger(w.data)}
                </Text>
                {w.data.enabled === false && (
                  <StatusBadge tone="warning">
                    {ingressCopy.workflows.paused}
                  </StatusBadge>
                )}
                {w.data.recipe != null && (
                  <RecipeTag workspaceId={workspaceId} tag={w.data.recipe} />
                )}
                {w.byAgent && (
                  <Text variant="caption" tone="text-mute">
                    {ingressCopy.workflows.byAgent}
                  </Text>
                )}
                <span className={styles.actions}>
                  <Button
                    variant="ghost"
                    aria-label={ingressCopy.workflows.removeName(
                      name(w.data.agent_id),
                    )}
                    loading={
                      removeWorkflow.isPending &&
                      removeWorkflow.variables === w.id
                    }
                    onClick={() => removeWorkflow.mutate(w.id)}
                  >
                    {ingressCopy.workflows.remove}
                  </Button>
                </span>
              </RowListItem>
            ))}
            {adding ? (
              <RowListItem className={styles.form}>
                <form aria-label={ingressCopy.workflows.add} onSubmit={submit}>
                  <Stack gap="space-12">
                    <Grid columns={1} mdColumns={2} gap="space-16">
                      <SelectField
                        id={`workflow-agent-${topicId}`}
                        label={ingressCopy.workflows.agent}
                        value={agentId}
                        onValue={setAgentId}
                      >
                        <option value="">
                          {ingressCopy.workflows.chooseAgent}
                        </option>
                        {agents
                          .filter((a) => a.status === 'active')
                          .map((a) => (
                            <option key={a.agent_id} value={a.agent_id}>
                              {a.name}
                            </option>
                          ))}
                      </SelectField>
                      <TextField
                        id={`workflow-type-${topicId}`}
                        label={ingressCopy.workflows.eventType}
                        help={ingressCopy.workflows.eventTypeHelp}
                        value={eventType}
                        onValue={setEventType}
                      />
                    </Grid>
                    <TextareaField
                      id={`workflow-instructions-${topicId}`}
                      label={ingressCopy.workflows.instructions}
                      hint={ingressCopy.workflows.optional}
                      value={instructions}
                      onValue={setInstructions}
                      textareaProps={{ rows: 3 }}
                    />
                    <Inline gap="space-8">
                      <Button
                        type="submit"
                        loading={add.isPending}
                        disabled={!agentId || !eventType.trim()}
                      >
                        {ingressCopy.workflows.add}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={closeForm}
                      >
                        {ingressCopy.workflows.cancel}
                      </Button>
                    </Inline>
                  </Stack>
                </form>
              </RowListItem>
            ) : (
              <RowListItem>
                <Button variant="ghost" onClick={() => setAdding(true)}>
                  <Icon name="plus" size="icon-embedded" />
                  {ingressCopy.workflows.add}
                </Button>
              </RowListItem>
            )}
          </RowList>
        </>
      )}
      {failed && <Alert tone="danger">{failed}</Alert>}
    </Stack>
  )
}

/** "from a recipe", linking to its setup when the tag names the install. */
function RecipeTag({
  workspaceId,
  tag,
}: {
  workspaceId: string
  tag: unknown
}) {
  const install =
    tag && typeof tag === 'object' && 'install' in tag
      ? (tag as { install?: unknown }).install
      : undefined
  if (typeof install !== 'string') {
    return (
      <Text variant="caption" tone="text-mute">
        {ingressCopy.workflows.fromRecipe}
      </Text>
    )
  }
  return (
    <Link variant="quiet" href={ROUTES.recipeInstall(workspaceId, install)}>
      {ingressCopy.workflows.fromRecipe}
    </Link>
  )
}
